import React, { useEffect, useMemo, useState } from "react";
import { accessApi } from "../../services/accessApi";

const EMPTY_FORM = {
  id: null,
  name: "",
  slug: "",
  html_template: "",
  css_styles: "",
  is_active: true,
  is_default: false,
  allowed_domain: "modularobjects.com",
};

export default function ITMailSignatureTemplatesPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await accessApi.getMailSignatureTemplates();
      setRows(res?.data?.items || res?.data?.data || []);
    } catch (err) {
      console.error("ITMailSignatureTemplatesPage load error:", err);
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to load templates.");
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((row) =>
      [
        row.name,
        row.slug,
        row.allowed_domain,
        row.html_template,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [rows, q]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditorOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      name: row.name || "",
      slug: row.slug || "",
      html_template: row.html_template || "",
      css_styles: row.css_styles || "",
      is_active: Number(row.is_active) === 1 || row.is_active === true,
      is_default: Number(row.is_default) === 1 || row.is_default === true,
      allowed_domain: row.allowed_domain || "modularobjects.com",
    });
    setEditorOpen(true);
  };

  const saveTemplate = async () => {
    try {
      setSaving(true);
      setError("");

      const payload = {
        name: form.name,
        slug: form.slug || null,
        html_template: form.html_template,
        css_styles: form.css_styles,
        is_active: !!form.is_active,
        is_default: !!form.is_default,
        allowed_domain: form.allowed_domain || "modularobjects.com",
      };

      if (form.id) {
        await accessApi.updateMailSignatureTemplate(form.id, payload);
      } else {
        await accessApi.createMailSignatureTemplate(payload);
      }

      setEditorOpen(false);
      await load();
    } catch (err) {
      console.error("saveTemplate error:", err);
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  const setDefault = async (id) => {
    try {
      await accessApi.setDefaultMailSignatureTemplate(id);
      await load();
    } catch (err) {
      console.error("setDefault template error:", err);
      alert(err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to set default template.");
    }
  };

  const generateAll = async (id) => {
    if (!window.confirm("Generate signatures for all domain users from this template?")) return;

    try {
      const res = await accessApi.generateAllMailSignaturesFromTemplate(id);
      const data = res?.data || {};
      alert(`Done. OK: ${data.ok ?? data.data?.ok ?? 0}, Failed: ${data.failed ?? data.data?.failed ?? 0}`);
      await load();
    } catch (err) {
      console.error("generateAll error:", err);
      alert(err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to generate signatures.");
    }
  };

  const removeTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;

    try {
      await accessApi.deleteMailSignatureTemplate(id);
      await load();
    } catch (err) {
      console.error("deleteTemplate error:", err);
      alert(err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to delete template.");
    }
  };

  if (loading) return <div className="p-6 text-black dark:text-white">Loading templates...</div>;
  if (error && !editorOpen && !previewOpen) {
    return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">Mail Signature Templates</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage corporate signature templates and generate signatures for domain users.
          </p>
        </div>

        <button
          onClick={openCreate}
          className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium"
        >
          + New Template
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search templates..."
          className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
        />
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Slug</th>
                <th className="text-left p-3">Domain</th>
                <th className="text-left p-3">Active</th>
                <th className="text-left p-3">Default</th>
                <th className="text-left p-3">Updated</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60"
                >
                  <td className="p-3 text-black dark:text-white font-medium">{row.name}</td>
                  <td className="p-3 text-black dark:text-white">{row.slug || "-"}</td>
                  <td className="p-3 text-black dark:text-white">{row.allowed_domain || "-"}</td>
                  <td className="p-3 text-black dark:text-white">{Number(row.is_active) === 1 || row.is_active === true ? "Active" : "Inactive"}</td>
                  <td className="p-3 text-black dark:text-white">{Number(row.is_default) === 1 || row.is_default === true ? "Yes" : "No"}</td>
                  <td className="p-3 text-black dark:text-white">{row.updated_at || "-"}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openEdit(row)}
                        className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => {
                          setForm({
                            id: row.id,
                            name: row.name || "",
                            slug: row.slug || "",
                            html_template: row.html_template || "",
                            css_styles: row.css_styles || "",
                            is_active: Number(row.is_active) === 1 || row.is_active === true,
                            is_default: Number(row.is_default) === 1 || row.is_default === true,
                            allowed_domain: row.allowed_domain || "modularobjects.com",
                          });
                          setPreviewOpen(true);
                        }}
                        className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
                      >
                        Preview
                      </button>

                      <button
                        onClick={() => setDefault(row.id)}
                        className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
                      >
                        Set Default
                      </button>

                      <button
                        onClick={() => generateAll(row.id)}
                        className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
                      >
                        Generate All
                      </button>

                      <button
                        onClick={() => removeTemplate(row.id)}
                        className="px-3 py-2 rounded-xl border border-red-300 text-red-700 bg-red-50 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    No templates found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editorOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-neutral-800">
              <h2 className="text-xl font-bold text-black dark:text-white">
                {form.id ? "Edit Template" : "New Template"}
              </h2>

              <button
                onClick={() => setEditorOpen(false)}
                className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
              >
                Close
              </button>
            </div>

            <div className="p-6 space-y-4">
              {error ? (
                <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900 px-4 py-3">
                  {error}
                </div>
              ) : null}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm mb-1 text-black dark:text-white">Name</div>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                  />
                </div>

                <div>
                  <div className="text-sm mb-1 text-black dark:text-white">Slug</div>
                  <input
                    value={form.slug}
                    onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                    className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                  />
                </div>

                <div>
                  <div className="text-sm mb-1 text-black dark:text-white">Allowed Domain</div>
                  <input
                    value={form.allowed_domain}
                    onChange={(e) => setForm((prev) => ({ ...prev, allowed_domain: e.target.value }))}
                    className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
                  />
                </div>

                <div className="flex gap-6 items-end">
                  <label className="flex items-center gap-2 text-sm text-black dark:text-white">
                    <input
                      type="checkbox"
                      checked={!!form.is_active}
                      onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                    />
                    Active
                  </label>

                  <label className="flex items-center gap-2 text-sm text-black dark:text-white">
                    <input
                      type="checkbox"
                      checked={!!form.is_default}
                      onChange={(e) => setForm((prev) => ({ ...prev, is_default: e.target.checked }))}
                    />
                    Default
                  </label>
                </div>
              </div>

              <div>
                <div className="text-sm mb-1 text-black dark:text-white">HTML Template</div>
                <textarea
                  value={form.html_template}
                  onChange={(e) => setForm((prev) => ({ ...prev, html_template: e.target.value }))}
                  rows={18}
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white font-mono"
                />
              </div>

              <div>
                <div className="text-sm mb-1 text-black dark:text-white">CSS Styles</div>
                <textarea
                  value={form.css_styles}
                  onChange={(e) => setForm((prev) => ({ ...prev, css_styles: e.target.value }))}
                  rows={10}
                  className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white font-mono"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setEditorOpen(false)}
                  className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
                >
                  Cancel
                </button>

                <button
                  onClick={saveTemplate}
                  disabled={saving}
                  className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Save Template"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-neutral-800">
              <h2 className="text-xl font-bold text-black dark:text-white">Template Preview</h2>

              <button
                onClick={() => setPreviewOpen(false)}
                className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
              >
                Close
              </button>
            </div>

            <div className="p-6">
              <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-white dark:bg-neutral-900 overflow-auto">
                <style>{form.css_styles || ""}</style>
                <div dangerouslySetInnerHTML={{ __html: form.html_template || "<em>No template HTML</em>" }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}